import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import Biologicals from '@/pages/diagnostic/components/Biologicals'
import { consultationOfflineStore, documentOfflineStore, OfflineConsultation } from '@/core/offline'
import { getApiErrorMessage } from '@/utils/errors'

type OfflineBiologicalsProps = {
    consultation: OfflineConsultation
    onConsultationUpdate: (consultation: OfflineConsultation) => void
}

export function OfflineBiologicals({ consultation, onConsultationUpdate }: OfflineBiologicalsProps) {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()

    const [isCreatingBiological, setIsCreatingBiological] = useState(false)
    const [isDeletingBiological, setIsDeletingBiological] = useState(false)
    const [isDeletingBiologicalFiles, setIsDeletingBiologicalFiles] = useState(false)

    const biologicals = useMemo(() => {
        if (!consultation) return []

        return _.map(consultation.biologicals || [], (biological) => ({
            ...biological,
            files: consultation.files?.filter((file) =>
                biological.files?.includes(file.key) || file.key === biological.file
            ) || [],
        }))
    }, [consultation])

    const handleCreateBiological = async (values: any) => {
        if (!consultation._id) return

        try {
            setIsCreatingBiological(true)
            const biologicalWithId = {
                ...values,
                id: uuidv4(),
                files: []
            }

            const updatedConsultation = await consultationOfflineStore.addBiological(consultation._id, biologicalWithId)
            onConsultationUpdate(updatedConsultation)
            enqueueSnackbar(t('notification.consultation.biological.create.success'), { variant: 'success' })
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.biological.create.error')), { variant: 'error' })
        } finally {
            setIsCreatingBiological(false)
        }
    }

    const handleUploadBiologicalFile = async (file: File, biologicalId: string) => {
        if (!consultation._id) return

        try {
            // Convert file to base64 for offline storage
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => resolve(reader.result as string)
                reader.onerror = reject
                reader.readAsDataURL(file)
            })

            // Create offline document
            const offlineDocument = await documentOfflineStore.createOfflineDocument({
                filename: file.name,
                size: file.size,
                mimeType: file.type,
                base64Data: base64,
                consultationId: consultation._id,
                biologicalId
            })

            // Update consultation with new file
            const updatedConsultation = await consultationOfflineStore.get(consultation._id)
            if (updatedConsultation) {
                onConsultationUpdate(updatedConsultation)
            }

            enqueueSnackbar(t('notification.consultation.biological.create.success'), { variant: 'success' })
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.biologicalFile.upload.error')), { variant: 'error' })
        }
    }

    const handleDeleteBiological = async (biologicalId: string) => {
        if (!consultation._id) return

        try {
            setIsDeletingBiological(true)
            const updatedConsultation = await consultationOfflineStore.removeBiological(consultation._id, biologicalId)
            onConsultationUpdate(updatedConsultation)
            enqueueSnackbar(t('notification.consultation.biological.delete.success'), { variant: 'success' })
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.biological.delete.error')), { variant: 'error' })
        } finally {
            setIsDeletingBiological(false)
        }
    }

    const handleDeleteBiologicalFiles = async (biologicalId: string, files: any[]) => {
        if (!consultation._id) return

        try {
            setIsDeletingBiologicalFiles(true)

            // Delete documents from offline store
            for (const file of files) {
                await documentOfflineStore.delete(file.key)
            }

            // Refresh consultation data
            const updatedConsultation = await consultationOfflineStore.get(consultation._id)
            if (updatedConsultation) {
                onConsultationUpdate(updatedConsultation)
            }

            enqueueSnackbar(t('notification.consultation.biologicalFile.delete.success'), { variant: 'success' })
        } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.biologicalFile.delete.error')), { variant: 'error' })
        } finally {
            setIsDeletingBiologicalFiles(false)
        }
    }

    if (!consultation._id) return null

    return (
        <Biologicals
            biologicals={biologicals}
            isCreatingBiological={isCreatingBiological}
            isDeletingBiological={isDeletingBiological}
            isDeletingBiologicalFiles={isDeletingBiologicalFiles}
            onCreateBiological={handleCreateBiological}
            onUploadBiologicalFile={handleUploadBiologicalFile}
            onDeleteBiological={handleDeleteBiological}
            onDeleteBiologicalFiles={handleDeleteBiologicalFiles}
            readOnly={false}
        />
    )
}

export default OfflineBiologicals 